/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;
      this.countUpdateTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();
      this.addDesktopCloseListener();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

addListeners() {
  if (this.filteringEnabled) {
    this.breakpointChangeHandler = this.breakpointChangeHandler
      || this.handleBreakpointChange.bind(this);
    this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
    this.filters.addEventListener('input', this.handleFilterInput.bind(this));
    this.filters.addEventListener('change', this.handleFilterInput.bind(this));
    this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
    window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
  }

  if (this.sortingEnabled) {
    this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
  }

  const applyBtn = this.querySelector('.js-apply-filters');
  if (applyBtn) {
    applyBtn.addEventListener('click', () => {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);

      const emptyParams = [];
      searchParams.forEach((value, key) => {
        if (!value) emptyParams.push(key);
      });
      emptyParams.forEach((key) => searchParams.delete(key));

      if (this.sortingEnabled) {
        searchParams.set('sort_by', searchParams.get('sort_by') || '');
      }

      this.applyFilters(searchParams.toString(), null).then(() => {
        if (!theme.mediaMatches.lg) this.close();
      });
    });
  }
}

    /**
     * Adds a click handler so the drawer's close (X) button also works on
     * desktop, where the filters column is closed via the toolbar's Filter
     * toggle rather than the mobile drawer's open/close mechanism. Bound
     * once here (not in addListeners, which re-runs after every filter
     * update) to avoid attaching duplicate listeners over time.
     */
    addDesktopCloseListener() {
      const closeBtn = this.querySelector('.js-close-drawer');
      if (!closeBtn) return;

      closeBtn.addEventListener('click', () => {
        if (theme.mediaMatches.lg) {
          const filterToggle = document.querySelector('.js-toggle-filters');
          if (filterToggle) filterToggle.click();
        }
      });
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Routes filter input/change events based on viewport.
     * Desktop (lg and up) has no apply button, so selecting a filter reloads
     * the grid immediately. Mobile keeps the drawer's apply-button flow and
     * only refreshes the result count until the button is pressed.
     * The breakpoint is checked per event rather than at bind time so that
     * resizing the window switches behaviour without rebinding listeners.
     * @param {object} evt - Event object.
     */
    handleFilterInput(evt) {
      if (theme.mediaMatches.lg) {
        this.handleFilterChange(evt);
      } else {
        this.handleCountUpdate(evt);
      }
    }

    /**
     * Handles filter input/change events for count update only (no grid reload).
     * @param {object} evt - Event object.
     */
    handleCountUpdate(evt) {
      if (evt.type === 'change' && !evt.target.id?.includes('price-range')) return;
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      clearTimeout(this.countUpdateTimeout);
      this.countUpdateTimeout = setTimeout(() => {
        this.updateResultCount();
      }, 400);
    }

    /**
     * Fetches updated result count and updates the apply button text.
     */
async updateResultCount() {
  const formData = new FormData(this.form);
  const searchParams = new URLSearchParams(formData);

  const emptyParams = [];
  searchParams.forEach((value, key) => {
    if (!value) emptyParams.push(key);
  });
  emptyParams.forEach((key) => searchParams.delete(key));

  const fetchUrl = `${window.location.pathname}?${searchParams}&section_id=${this.form.dataset.filterSectionId}`;

  try {
    const response = await fetch(fetchUrl);
    if (!response.ok) return;

    const tmpl = document.createElement('template');
    tmpl.innerHTML = await response.text();

    // Update button count
    const newBtn = tmpl.content.querySelector('.js-apply-filters');
    const applyBtn = this.querySelector('.js-apply-filters');
    if (newBtn && applyBtn) {
      applyBtn.innerText = newBtn.innerText;
    }

    // Capture open state of ALL details elements before replacing
    const openStates = {};
    this.form.querySelectorAll('details').forEach((detail) => {
      if (detail.id) openStates[detail.id] = detail.open;
    });

    // Capture scroll position of drawer content
    const drawerContent = this.querySelector('.drawer__content');
    const scrollTop = drawerContent ? drawerContent.scrollTop : 0;

    // Update filter form
    const newForm = tmpl.content.getElementById('facets');
    if (newForm) {
      this.form.innerHTML = newForm.innerHTML;

      // Restore open state of all details elements
      this.form.querySelectorAll('details').forEach((detail) => {
        if (detail.id && openStates[detail.id] !== undefined) {
          detail.open = openStates[detail.id];
        }
      });

      this.addElements();
      this.addListeners();
    }

    // Restore scroll position
    if (drawerContent) drawerContent.scrollTop = scrollTop;

  } catch (error) {
    console.warn(error);
  }
}

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        const activeElementId = document.activeElement.id;

        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        this.results.classList.add('is-loading');

        // Hidden on desktop, so every reference below is guarded.
        const closeBtn = this.querySelector('.js-apply-filters');
        if (closeBtn) {
          closeBtn.ariaDisabled = 'true';
          closeBtn.classList.add('is-loading');
        }

        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          const newApplyBtn = tmpl.content.querySelector('.js-apply-filters');
          if (closeBtn && newApplyBtn) {
            closeBtn.innerText = newApplyBtn.innerText;
          }

          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          this.addElements();
          this.addListeners();

          if (customPagination && customPagination.reload) customPagination.reload();

          if (updateUrl) FacetFilters.updateURL(searchParams);

          if (this.results.getBoundingClientRect().top < 0) {
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          if (closeBtn) {
            closeBtn.classList.remove('is-loading');
            closeBtn.removeAttribute('aria-disabled');
          }

          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          if (activeElementId) document.getElementById(activeElementId)?.focus();

          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error);
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
